
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { User } from 'src/app/model/user';
import { UntypedFormGroup, UntypedFormBuilder, Validators, ValidatorFn, AbstractControl, ValidationErrors, UntypedFormControl } from '@angular/forms';
import { AuthProvider } from 'src/app/providers/auth.provider';
import { ActivatedRoute, Router } from '@angular/router';
import { BusinessType } from '../../model/enums/business.type';
import { CommonProvider, ReportProvider } from '../../providers';
import { YesNo } from 'src/app/model/Enums';
import { CommonService, ToasterService, UserService } from 'src/app/services';
import { fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

declare var grecaptcha: any;

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss']
})
export class RegisterComponent implements OnInit {
  code: string;
  PROMO: string;
  user: User;
  isCodeValid: boolean = false;
  stepNumber: number = 1;
  personalInfoForm: UntypedFormGroup;
  addressForm: UntypedFormGroup;
  userObject: User = new User();
  whatDoYouDo: BusinessType;
  submitting: boolean = false;
  captchaVisible: boolean = false;
  BusinessType = BusinessType;
  isValidPromo: boolean = false;
  checkingPromo: boolean = false;

  anotherCompany: YesNo = YesNo.NO;
  showSuccessPage: boolean = false;

  hasPassword: boolean = true;

  inputSearchSub: any = null;
  @ViewChild('txtPromo') set inputText(inputPromoRef: ElementRef) {
    if (inputPromoRef) {
      this.inputSearchSub = fromEvent(inputPromoRef.nativeElement, 'keyup')
        .pipe(
          map(() => this.addressForm.get('PROMO_CODE')?.value),
          debounceTime(1000),
          distinctUntilChanged());

      this.inputSearchSub.subscribe(() => {
        if (this.user.BUSINESS_TYPE === BusinessType.BROKER) {
          this.checkPromoCode();
        }
      });
    }
  }

  constructor(
    private fb: UntypedFormBuilder,
    private authProvider: AuthProvider,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToasterService,
    private userService: UserService,
    private commonProvider: CommonProvider,
    public commonService: CommonService
  ) {
  }

  ngOnInit(): void {
    this.createForm();

    // Get Code from the routing params
    this.code = this.route.snapshot.queryParams.code;

    this.route.queryParams
      .subscribe(params => {
          if (params?.promo) {
            this.PROMO = params.promo;
          }
        }
      );

    if (!this.code) {
      this.isCodeValid = false;
    } else {
      this.isCodeValid = true;
    }

    // Check code is valid or not
    this.authProvider.getUserByCode(this.code).subscribe(response => {
      if (response.success) {
        this.isCodeValid = true;
        this.user = User.parse(response.result);
        this.stepNumber = 1;
        if (this.user?.ORGANIZATION?.ID) {
          this.router.navigateByUrl(`/abc/register?code=${this.code}`);
        }

        setTimeout(() => {
          if(this.user.REQUIRE_PASS_NO_REGISTER){
            this.personalInfoForm.controls['phone'].clearValidators();
            this.personalInfoForm.controls['phone'].updateValueAndValidity();

            this.addressForm.controls['zip'].clearValidators();
            this.addressForm.controls['zip'].updateValueAndValidity();
          }

          if (response.result.HAS_PASSWORD) {
            this.hasPassword = true;
            this.personalInfoForm.controls['password'].clearValidators();
          } else {
            this.hasPassword = false;
            this.personalInfoForm.controls['password'].setValidators([Validators.required, Validators.pattern('^(?=.*[A-Za-z])(?=.*\\d)(?=.*[$@$!%*#?&])[A-Za-z\\d$@$!%*#?&]{8,}$')]);
          }
          this.personalInfoForm.controls['password'].updateValueAndValidity();
        }, 10);

        if(this.user.BUSINESS_TYPE == BusinessType.BROKER) {
          this.addressForm.addControl('PROMO_CODE', new UntypedFormControl(''));
        }
      } else {
        /** If code is invalid Route user to login(default) page */
        this.isCodeValid = false;
        setTimeout(() => { this.router.navigate(['/']); }, 4000);
      }
    }, err => {
      console.error(err);
    });
  }

  /**
   * @description create form
   */
  createForm() {
    this.personalInfoForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      phone: ['', Validators.compose([Validators.required, Validators.minLength(12)])],
      password: [''],
      orgName: ['']
    });
    this.addressForm = this.fb.group({
      address1: [''],
      // address2: [''],
      city: [''],
      state: [''],
      zip: ['', [Validators.minLength(5), Validators.maxLength(5)]],
      isAgree: [false, [Validators.requiredTrue]]
    });
  }

  /**
   * @description Validate phone number
   */
  validatePhone(): ValidatorFn {
    return (c: AbstractControl): ValidationErrors | null => {
      if (c.value === null || c.value.length === 0) {
        return null;
      }
      let abc = c.value.replace(/ /g, '');
      const phone: number = Number(abc);
      return !phone ? { error: true } : null;
    };
  }

  /**
   * @description Go back to the previous page
   */
  goBack(): void {
    this.stepNumber = 1;
  }

  /**
   * @description is company radio button change
   */
  isCompanyChange(isCmpny: boolean) {
    if (isCmpny) {
      this.personalInfoForm.controls['orgName'].setValidators([Validators.required]);
    } else {
      this.personalInfoForm.controls['orgName'].clearValidators();
      this.personalInfoForm.controls['whatYouDo'].clearValidators();
    }
    this.personalInfoForm.controls['orgName'].updateValueAndValidity();
    this.personalInfoForm.controls['whatYouDo'].updateValueAndValidity();
  }

  /**
   * @description validation of control
   */
  checkControlValid(controlName: string) {
    const controlDetails = this.addressForm?.get(controlName);
    if (!controlDetails) {
      return false;
    }
    return (controlDetails?.touched || controlDetails?.dirty) && controlDetails?.invalid;
  }

  /**
   * @description validation of control
   */
  checkPersonalInfoControlValid(controlName: string) {
    const controlDetails = this.personalInfoForm.get(controlName);
    if (!controlDetails) {
      return false;
    }
    return (controlDetails.touched || controlDetails.dirty) && controlDetails.invalid;
  }

  /**
   * @description goto next step
   */
  nextStep() {
    if (this.captchaVisible) {
      grecaptcha.reset();
    }
    this.stepNumber++;
  }

  /**
   * @description personal data save
   */
  personalSave() {
    if (this.personalInfoForm.valid) {
      this.userObject.NAME_FIRST = this.personalInfoForm.controls['firstName'].value;
      this.userObject.NAME_LAST = this.personalInfoForm.controls['lastName'].value;
      this.userObject.TELEPHONE = this.personalInfoForm.controls['phone'].value;
      this.userObject.COMPANY = this.personalInfoForm.controls['orgName'].value;
      /*if (this.personalInfoForm.controls['isOtherCompany'].value) {
        this.userObject.BUSINESS_TYPE = this.whatDoYouDo;
      } else {
        this.userObject.COMPANY = '';
        this.userObject.BUSINESS_TYPE = null;
      }*/
      this.nextStep();
    }
    window.scrollTo(0,0);
  }

  /**
   * @description address save
   */
  addressSave() {
    if (this.addressForm.valid) {
      this.submitting = true;
      if(this.user?.REQUIRE_PASS_NO_REGISTER){
        this.userObject.NAME_FIRST = this.personalInfoForm.controls['firstName'].value;
        this.userObject.NAME_LAST = this.personalInfoForm.controls['lastName'].value;
        this.userObject.COMPANY = this.personalInfoForm.controls['orgName'].value;
      }else if(!this.user?.REQUIRE_PASS_NO_REGISTER) {
        this.userObject.ADDRESS = this.addressForm.controls['address1'].value;
        // this.userObject.ADDRESS_EXT = this.addressForm.controls['address2'].value;
        this.userObject.STATE = this.addressForm.controls['state'].value;
        this.userObject.CITY = this.addressForm.controls['city'].value;
        this.userObject.ZIP = this.addressForm.controls['zip'].value;
      }

      const params: any = {
        USER: this.userObject
      };

      if (this.user.BUSINESS_TYPE === BusinessType.BROKER && this.addressForm.controls['PROMO_CODE'].value){
        params.PROMO_CODE = this.addressForm.controls['PROMO_CODE'].value;
      }

      if (!this.hasPassword) {
        params.PASSWORD = this.personalInfoForm.controls['password'].value;
      }

      if(this.PROMO){
        params.PROMO = this.PROMO;
      }

      this.authProvider.register(params, this.code).subscribe((response) => {
        this.submitting = false;
        if (response.success) {
          this.showSuccessPage = true;
          this.personalInfoForm.reset();
          this.addressForm.reset();

          localStorage.setItem('IV-TOKEN', response.result.token);
          this.userService.setUser(User.parse(response.result.profile));
          setTimeout(() => {
            this.router.navigate(['/dashboard']);
          }, 4000);
        } else {
          this.captchaVisible = response.captcha;
          if (this.captchaVisible) {
            grecaptcha.reset();
          }
          this.toastr.error(response.error.txt);
        }
      }, () => {
        this.submitting = false;
      })
    }
  }

  /**
   * @description Change event
   */
  changeEvent(event: any): void {
    this.whatDoYouDo = BusinessType[<string>event];
  }

  /**
   * @description Get address info based on city state
   */
  getAddressInfo(): void {
    if (this.addressForm.controls['zip'].value?.length === 5) {
      this.commonProvider.identifyZip(this.addressForm.controls['zip'].value).subscribe(resp => {
        if (resp.success) {
          if (resp.result) {
            this.addressForm.patchValue({
              state: this.getFullStateName(resp.result.STATE),
              city: resp.result.CITY
            });
            this.userObject.STATE = this.addressForm.controls['state'].value;
            this.userObject.CITY = this.addressForm.controls['city'].value;
          } else {
            this.addressForm.patchValue({
              zip: ''
            });
          }
        } else {
          this.addressForm.patchValue({
            zip: ''
          });
        }
      }, err => {
        // Error Handling
      });
    }
  }

  /** to get state name */
  getFullStateName(stateCode: string): string {
    let stateName = stateCode;
    switch (stateCode) {
      case 'AL':
        stateName = 'Alabama';
        break;
      case 'AK':
        stateName = 'Alaska';
        break;
      case 'AZ':
        stateName = 'Arizona';
        break;
      case 'AR':
        stateName = 'Arkansas';
        break;
      case 'CA':
        stateName = 'California';
        break;
      case 'CO':
        stateName = 'Colorado';
        break;
      case 'CT':
        stateName = 'Connecticut';
        break;
      case 'DE':
        stateName = 'Delaware';
        break;
      case 'FL':
        stateName = 'Florida';
        break;
      case 'GA':
        stateName = 'Georgia';
        break;
      case 'GU':
        stateName = 'Guam';
        break;
      case 'HI':
        stateName = 'Hawaii';
        break;
      case 'ID':
        stateName = 'Idaho';
        break;
      case 'IL':
        stateName = 'Illinois';
        break;
      case 'IN':
        stateName = 'Indiana';
        break;
      case 'IA':
        stateName = 'Iowa';
        break;
      case 'KS':
        stateName = 'Kansas';
        break;
      case 'KY':
        stateName = 'Kentucky';
        break;
      case 'LA':
        stateName = 'Louisiana';
        break;
      case 'ME':
        stateName = 'Maine';
        break;
      case 'MD':
        stateName = 'Maryland';
        break;
      case 'MA':
        stateName = 'Massachusetts';
        break;
      case 'MI':
        stateName = 'Michigan';
        break;
      case 'MN':
        stateName = 'Minnesota';
        break;
      case 'MS':
        stateName = 'Mississippi';
        break;
      case 'MO':
        stateName = 'Missouri';
        break;
      case 'MT':
        stateName = 'Montana';
        break;
      case 'NE':
        stateName = 'Nebraska';
        break;
      case 'NV':
        stateName = 'Nevada';
        break;
      case 'NH':
        stateName = 'New Hampshire';
        break;
      case 'NJ':
        stateName = 'New Jersey';
        break;
      case 'NM':
        stateName = 'New Mexico';
        break;
      case 'NY':
        stateName = 'New York';
        break;
      case 'NC':
        stateName = 'North Carolina';
        break;
      case 'ND':
        stateName = 'North Dakota';
        break;
      case 'OH':
        stateName = 'Ohio';
        break;
      case 'OK':
        stateName = 'Oklahoma';
        break;
      case 'OR':
        stateName = 'Oregon';
        break;
      case 'PA':
        stateName = 'Pennsylvania';
        break;
      case 'PR':
        stateName = 'Puerto Rico';
        break;
      case 'RI':
        stateName = 'Rhode Island';
        break;
      case 'SC':
        stateName = 'South Carolina';
        break;
      case 'SD':
        stateName = 'South Dakota';
        break;
      case 'TN':
        stateName = 'Tennessee';
        break;
      case 'TX':
        stateName = 'Texas';
        break;
      case 'UT':
        stateName = 'Utah';
        break;
      case 'VT':
        stateName = 'Vermont';
        break;
      case 'VA':
        stateName = 'Virginia';
        break;
      case 'WA':
        stateName = 'Washington';
        break;
      case 'DC':
        stateName = 'District of Columbia';
        break;
      case 'WV':
        stateName = 'West Virginia';
        break;
      case 'WI':
        stateName = 'Wisconsin';
        break;
      case 'WY':
        stateName = 'Wyoming';
        break;
    }
    return stateName;
  }

  /** to check promo code */
  checkPromoCode(): void {
    this.checkingPromo = true;
    this.authProvider.checkBrokerPromo(this.addressForm.get('PROMO_CODE')?.value).subscribe((resp) => {
      if (resp.success) {
        this.isValidPromo = true;
      } else {
        this.isValidPromo = false;
      }
      this.checkingPromo = false;
    }, err => {
      // Error Handling
      this.isValidPromo = false;
      this.checkingPromo = false;
    });
  }
}
